/* eslint-disable @typescript-eslint/no-explicit-any */
import { UserType } from "@/common/types";
import AutoComplete from "@/components/autocomplete";
import CustomSelect from "@/components/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import useDebounce from "@/hooks/use-debounce";
import { useToast } from "@/hooks/use-toast";
import { getAllDoctors, inviteUser } from "@/redux/actions/usersActions";
import { RootState } from "@/redux/reducers";
import { isUserAdmin, isUserDoctor } from "@/utils/common.util";
import { LegacyRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

interface UserSheetProps {
  buttonRef: LegacyRef<HTMLButtonElement>;
  selectedUser: UserType;
  onClose: () => void;
}

const UserSheet = ({ buttonRef, selectedUser, onClose }: UserSheetProps) => {
  const roles = [
    { value: "admin", label: "Admin", enabled: isUserAdmin() },
    { value: "doctor", label: "Doctor", enabled: isUserAdmin() },
    {
      value: "patient",
      label: "Patient",
      enabled: isUserAdmin() || isUserDoctor(),
    },
  ];

  const dispatch: any = useDispatch();
  const { toast } = useToast();
  const [state, setState] = useState<UserType>({
    name: "",
    email: "",
    role: "",
    doctor: null,
    created_at: "",
  });
  const [error, setError] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [doctorSearchTerm, setDoctorSearchTerm] = useState<string>("");
  const debouncedDoctorSearchTerm = useDebounce(doctorSearchTerm, 500);

  const { doctors } = useSelector((state: RootState) => state.users);

  useEffect(() => {
    dispatch(getAllDoctors(debouncedDoctorSearchTerm));
  }, [debouncedDoctorSearchTerm]);

  useEffect(() => {
    setState(selectedUser);
  }, [selectedUser]);

  const handleChange = (id: string, value: string) => {
    setState((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const validateInput = () => {
    let errorMessage = "";
    if (!state.name || state.name === "") {
      errorMessage = "Name is required";
    } else if (!state.email || state.email === "") {
      errorMessage = "Email is required";
    } else if (!state.role || state.role === "") {
      errorMessage = "Role is required";
    } else if (
      state.role === "patient" &&
      !isUserDoctor() &&
      (!state.doctor || state.doctor === null)
    ) {
      errorMessage = "Doctor is required";
    } else {
      errorMessage = "";
    }
    setError(errorMessage);
    return errorMessage;
  };

  const handleSubmit = () => {
    const errorMessage = validateInput();
    if (!errorMessage) {
      dispatch(
        inviteUser(state, () => {
          onClose();
          toast({
            description: "User invited successfully",
          });
          setIsOpen(false);
        })
      );
    }
  };

  return (
    <div>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center justify-end">
          <SheetTrigger asChild>
            <Button ref={buttonRef} variant="secondary">
              Add User
            </Button>
          </SheetTrigger>
        </div>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Add User</SheetTitle>
            <SheetDescription>
              Provide the user and there details
            </SheetDescription>
          </SheetHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                onChange={(e) => handleChange("name", e.target.value)}
                id="name"
                className="col-span-3"
                value={state.name}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                onChange={(e) => handleChange("email", e.target.value)}
                id="email"
                className="col-span-3"
                value={state.email}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="role" className="text-right">
                Role
              </Label>
              <div className="col-span-3">
                <CustomSelect
                  value={state.role}
                  options={roles.filter((role) => role.enabled)}
                  onChange={(option) => handleChange("role", option?.value)}
                  placeholder="Role"
                  labelKey="label"
                  valueKey="value"
                />
              </div>
            </div>
            {state.role === "patient" && !isUserDoctor() && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="role" className="text-right">
                  Doctor
                </Label>
                <div className="col-span-3">
                  <AutoComplete
                    placeHolder="doctor"
                    options={doctors as UserType[]}
                    labelKey="name"
                    onSelect={(option) => handleChange("doctor", option?.id)}
                    onChange={(search) => setDoctorSearchTerm(search)}
                    listCount={5}
                  />
                </div>
              </div>
            )}
            {error && (
              <div className="grid grid-cols-4 items-center gap-4">
                <div className="col-span-1"></div>
                <div className="col-span-3">
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              </div>
            )}
          </div>
          <SheetFooter>
            {/* <SheetClose asChild> */}
            <Button
              disabled={!state.name || !state.email || !state.role}
              type="submit"
              onClick={() => handleSubmit()}
            >
              Add
            </Button>
            {/* </SheetClose> */}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default UserSheet;
